import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { getFileExtension, getFileType } from 'utils/FileUtils'
import useSandreList from 'utils/customHook/useSandreList'
import ToastrAction from 'toastr/actions/ToastrAction'
import i18n from 'simple-react-i18n'
import { isNil } from 'lodash'
import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid } from '@mui/material'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import Input from 'components/forms/Input'
import Select from 'components/forms/Select'
import Checkbox from 'components/forms/Checkbox'
import DtoFile from 'components/file/dto/DtoFile'
import { hasValue } from 'utils/NumberUtil'
import { PICTURES_EXTENSIONS } from 'components/file/constant/FileConstants'
import moment from 'moment'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import ReferencialFileAction from './actions/ReferencialFileAction'
import LoadingButton from '../../../components/buttons/LoadingButton'

const ImportDialog = ({
    isOpen = false,
    onClose = () => {},
    onValidate = () => {},
    refetchFiles,
    files = [],
    code,
    type,
}) => {
    const [name, setName] = useState('')
    const [content, setContent] = useState('')
    const [fileType, setFileType] = useState(0)
    const [active, setActive] = useState(false)
    const [isUploading, setIsUploading] = useState(false)

    const dispatch = useDispatch()


    const onImportFile = (e) => {
        const reader = new FileReader()
        const file = e.target.files[0]
        setName(file.name)
        reader.onload = upload => setContent(upload.target.result)
        reader.readAsDataURL(file)
    }

    const { sandreCode, title } = getFileType(content)
    const fileTypes = useSandreList(sandreCode)

    const saveFile = () => {
        if (files.some(f => f.name === name)) {
            dispatch(ToastrAction.warning(i18n.nameAlreadyExist))
            return
        }
        const extension = `.${getFileExtension(name)?.toLowerCase()}`
        const formattedFile = {
            id: -1,
            code,
            name,
            path: PICTURES_EXTENSIONS.includes(extension) ? `/PHOTOS/${type}/`: `/DOCUMENTS/${type}/`,
            dateMaj: moment.now(),
            content,
            nature: fileType,
            actif: active,
        }

        setIsUploading(true)
        dispatch(ReferencialFileAction.updateFile(formattedFile))
            .then(isSuccess => {
                if (isSuccess) {
                    refetchFiles()
                    onValidate()
                    onClose()
                }
            })
            .finally(() => setIsUploading(false))
    }

    const onCloseDialog = () => !isUploading && onClose()

    return (
        <Dialog
            open={isOpen}
            onClose={onCloseDialog}
            maxWidth='lg'
        >
            <DefaultDialogTitle
                title={i18n.importFile}
                onClose={onCloseDialog}
            />
            <DialogContent>
                <Card>
                    <CardContent>
                        <Grid container spacing={1} alignItems='center'>
                            <Grid size='auto'>
                                <Button
                                    variant='contained'
                                    fullWidth
                                    component='label'
                                    startIcon={<UploadFileIcon />}
                                    disabled={isUploading}
                                >
                                    {i18n.chooseFile}
                                    <input type='file' hidden onChange={onImportFile} />
                                </Button>
                            </Grid>
                            <Grid size='grow'>
                                <Input title={i18n.fileName} value={hasValue(name) ? name : i18n.pleaseSelectFile} disabled/>
                            </Grid>
                            {hasValue(name) && (
                                <Grid container size={12} alignItems='center'>
                                    <Grid size={7}>
                                        <Select
                                            label={title}
                                            options={fileTypes}
                                            value={fileType}
                                            onChange={setFileType}
                                            noNullValue
                                            keyLabel='name'
                                            disabled={isUploading}
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <Checkbox
                                            onChange={setActive}
                                            checked={active}
                                            label={i18n.active}
                                            disabled={isUploading}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </CardContent>
                </Card>
            </DialogContent>
            <DialogActions>
                <LoadingButton
                    onClick={saveFile}
                    variant='contained'
                    color='primary'
                    loading={isUploading}
                    disabled={isNil(content) || isNil(name) || isNil(fileType)}
                >
                    {i18n.validate}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

ImportDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    refetchFiles: PropTypes.func,
    files: PropTypes.arrayOf(PropTypes.instanceOf(DtoFile)),
    code: PropTypes.String,
    type: PropTypes.string,
}

export default ImportDialog